import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { ArrowRight, Check, Gift, Mail, FileText, Truck, Plus, Minus } from "lucide-react";

export default function ProductSelectionPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [project, setProject] = useState(null);
  const [product, setProduct] = useState("classic");
  const [addons, setAddons] = useState({ gift_box: false, extra_copy: 0, express_delivery: false, handwritten_note: false, digital_pdf: false });
  const [totals, setTotals] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [totalsError, setTotalsError] = useState(false);
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
      const [m, p] = await Promise.all([api.get("/meta/options"), api.get(`/projects/${projectId}`)]);
      setMeta(m.data);
      setProject(p.data);
      setProduct(m.data.products[p.data?.data?.selected_product] ? p.data.data.selected_product : "classic");
      try {
        const saved = JSON.parse(sessionStorage.getItem(`ks_addons_${projectId}`) || "null");
        if (saved?.addons) setAddons(saved.addons);
      } catch (_) { /* Invalid browser data must not block ordering. */ }
      } catch (_) { setLoadError(true); }
    })();
  }, [projectId]);

  useEffect(() => {
    if (!meta) return;
    const controller = new AbortController();
    setTotals(null); setTotalsError(false);
    const params = new URLSearchParams({
      product_type: product,
      gift_box: addons.gift_box,
      extra_copy: addons.extra_copy,
      express_delivery: addons.express_delivery,
      handwritten_note: addons.handwritten_note,
      digital_pdf: addons.digital_pdf,
    });
    api.post(`/orders/preview-totals?${params.toString()}`, null, { signal: controller.signal }).then(({ data }) => setTotals(data)).catch((error) => { if (error.code !== "ERR_CANCELED") setTotalsError(true); });
    return () => controller.abort();
  }, [product, addons, meta]);

  const proceed = async () => {
    if (!totals) return;
    setContinuing(true);
    try {
      await api.put(`/projects/${projectId}`, { data: { ...project.data, selected_product: product } });
      // pass addons via sessionStorage to checkout
      sessionStorage.setItem(`ks_addons_${projectId}`, JSON.stringify({ product, addons }));
      navigate(`/checkout/${projectId}`);
    } catch (e) {
      toast.error("Could not continue.");
    } finally { setContinuing(false); }
  };

  if (loadError) return <><SiteHeader/><main id="main-content" className="form-shell"><p role="alert" data-testid="product-load-error">We couldn’t load your book options.</p><button className="btn-primary mt-5" onClick={() => window.location.reload()} data-testid="product-retry">Try again</button></main></>;

  if (!meta || !project) return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="font-serif text-2xl text-ink">Loading…</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader showNav={false} />
      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="k-overline mb-3">Your keepsake · Format & extras</p>
        <h1 className="page-title" data-testid="product-heading">Pick your book and any extras.</h1>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product */}
          <div className="lg:col-span-2 space-y-5">
            {Object.entries(meta.products).map(([key, p]) => (
              <div key={key} data-testid={`pick-product-${key}`}
                className="w-full text-left rounded p-6 sm:p-7 border bg-ink text-cream border-ink">
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div className="flex-1">
                    <p className="eyebrow" style={{ color: "#C9BFC2" }}>Printed softcover</p>
                    <p className="font-serif text-2xl font-bold mt-1">{p.label}</p>
                    <ul className="product-card-list">
                      <li>Text-first softcover</li>
                      <li>Elegant writing with full-page illustrations</li>
                      <li>Portrait format</li>
                    </ul>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-3xl font-extrabold" data-testid={`product-price-${key}`}>₹{p.price.toLocaleString('en-IN')}</p>
                    <span className="inline-flex items-center gap-1 mt-2 text-xs px-2.5 py-1 rounded-full bg-cream text-chocolate"><Check size={12}/> Selected</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Add-ons */}
            <div className="k-card p-6">
              <p className="k-overline mb-4">Optional add-ons</p>
              <p className="text-xs text-inkMute mb-5" data-testid="addon-fulfilment-note">Please check gift packaging, PDF delivery and timing with us before ordering. Express delivery does not guarantee an arrival date.</p>
              <div className="space-y-3">
                <AddonRow icon={Gift} label="Gift box" price={meta.addons.gift_box} checked={addons.gift_box} onChange={(v) => setAddons((a) => ({ ...a, gift_box: v }))} testId="addon-gift-box" />
                <AddonRow icon={Mail} label="Handwritten note" price={meta.addons.handwritten_note} checked={addons.handwritten_note} onChange={(v) => setAddons((a) => ({ ...a, handwritten_note: v }))} testId="addon-handwritten" />
                <AddonRow icon={Truck} label="Express delivery" price={meta.addons.express_delivery} checked={addons.express_delivery} onChange={(v) => setAddons((a) => ({ ...a, express_delivery: v }))} testId="addon-express" />
                <AddonRow icon={FileText} label="Digital PDF copy" price={meta.addons.digital_pdf} checked={addons.digital_pdf} onChange={(v) => setAddons((a) => ({ ...a, digital_pdf: v }))} testId="addon-pdf" />
                <div className="flex items-center justify-between py-3 border-t border-lineSoft">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cream text-chocolate border border-lineSoft"><Plus size={16} /></span>
                    <div>
                      <p className="text-sm font-medium text-ink">Extra copies</p>
                      <p className="text-xs text-inkMute">₹{meta.addons.extra_copy.toLocaleString('en-IN')} per copy</p>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <button disabled={addons.extra_copy === 0} aria-label="Remove one extra copy" onClick={() => setAddons((a) => ({ ...a, extra_copy: Math.max(0, a.extra_copy - 1) }))} className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-lineSoft" data-testid="extra-minus"><Minus size={14} /></button>
                    <span className="w-6 text-center text-ink" data-testid="extra-count">{addons.extra_copy}</span>
                    <button aria-label="Add one extra copy" onClick={() => setAddons((a) => ({ ...a, extra_copy: a.extra_copy + 1 }))} className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-lineSoft" data-testid="extra-plus"><Plus size={14} /></button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="k-card p-6 self-start lg:sticky lg:top-24">
            <p className="k-overline mb-4">Order summary</p>
            <div className="text-sm text-ink space-y-2">
              <div className="flex justify-between"><span>{meta.products[product].label}</span><span>₹{meta.products[product].price.toLocaleString('en-IN')}</span></div>
              {totals?.breakdown?.map((b, i) => (
                <div key={i} className="flex justify-between text-inkMute"><span>{b.name}</span><span>₹{b.price.toLocaleString('en-IN')}</span></div>
              ))}
              <div className="k-divider my-3" />
              <div className="flex justify-between font-medium" aria-live="polite" data-testid="product-subtotal"><span>Subtotal</span><span>{totals ? `₹${totals.total_price.toLocaleString('en-IN')}` : "Updating…"}</span></div>
            </div>
            {totalsError && <p role="alert" className="field-error" data-testid="product-totals-error">Couldn’t confirm pricing. Please try again.</p>}
            <button onClick={proceed} disabled={!totals || continuing} className="btn-primary w-full mt-6" data-testid="proceed-checkout">
              Continue to checkout <ArrowRight size={16} className="ml-1.5" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function AddonRow({ icon: Icon, label, price, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cream text-chocolate border border-lineSoft"><Icon size={16} /></span>
        <div>
          <p className="text-sm font-medium text-ink">{label}</p>
          <p className="text-xs text-inkMute">₹{price.toLocaleString('en-IN')}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} data-testid={testId} />
    </div>
  );
}
